import { KeyRound, LoaderPinwheel, X, Shield } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import Notification from './Notification';
import { changePassword } from '../utils/authApi';
import Loading from './elements/Loading';
import Modal from './Modal';
import Button from './elements/Button';
import Input from './elements/Input';
import { validatePasswordStrength, getPasswordStrength } from '../utils/passwordValidator';

const ChangePasswordModal = ({ open, onClose, onSuccess }) => {
    const [notification, setNotification] = useState({ message: '', type: 'success' });
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState({});
    const [passwordStrength, setPasswordStrength] = useState('weak');
    const [passwordErrors, setPasswordErrors] = useState([]);

    useEffect(() => {
        if (!open) return;
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
    }, [open]);

    useEffect(() => {
        setFormError({});
    }, [currentPassword, newPassword, confirmPassword]);

    const handleChangePassword = async (currentPassword, newPassword) => {
        try {
            const result = await changePassword(currentPassword, newPassword);
            if (result && result.success) {
                onSuccess();
            } else {
                if (result.code === 'INCORRECT_PASSWORD') {
                    setFormError({ ...formError, currentPassword: 'Incorrect' });
                    setNotification({ message: result.message || 'Failed to change password.', type: 'error' });
                } else if (result.code === 'WEAK_PASSWORD') {
                    setFormError({ ...formError, newPassword: 'Weak password' });
                    setNotification({ message: result.message || 'Failed to change password.', type: 'error' });
                } else {
                    setNotification({ message: result.message || 'Failed to change password.', type: 'error' });
                }
            }
            return result;
        } catch {
            setNotification({ message: 'Error changing password. Please try again later.', type: 'error' });
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!currentPassword) {
            errors.currentPassword = 'Required';
        }
        if (!newPassword) {
            errors.newPassword = 'Required';
        } else {
            // Validate password strength
            const validation = validatePasswordStrength(newPassword);
            if (!validation.isValid) {
                errors.newPassword = 'Weak password';
            }
        }
        if (!confirmPassword) {
            errors.confirmPassword = 'Required';
        }
        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            errors.confirmPassword = 'Does not match';
        }
        setFormError(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const result = await handleChangePassword(currentPassword, newPassword);
            if (result && result.success) {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                onClose();
            } else {
                setNotification({ message: result && result.message ? result.message : 'Failed to change password.', type: 'error' });
            }
        } catch (err) {
            setNotification({ message: 'Error changing password.', type: 'error' });
        }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} icon={<KeyRound />} title="Change Password">
            <Notification message={notification.message} onClose={() => setNotification({ message: '', type: 'success' })} type={notification.type} />
            <form className="flex flex-col gap-3 font-sans">
                <Input
                    label="Current Password"
                    type="password"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    placeholder="Your current password"
                    required
                    autoFocus
                    error={formError.currentPassword}
                />
                <div>
                    <Input
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={e => {
                            const newPwd = e.target.value;
                            setNewPassword(newPwd);
                            const validation = validatePasswordStrength(newPwd);
                            setPasswordErrors(validation.errors);
                            setPasswordStrength(getPasswordStrength(newPwd));
                        }}
                        placeholder="Your new password"
                        required
                        error={formError.newPassword}
                    />
                    {newPassword && (
                        <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                <span className={`text-xs font-medium ${
                                    passwordStrength === 'strong' ? 'text-green-600' :
                                    passwordStrength === 'medium' ? 'text-yellow-600' :
                                    'text-red-600'
                                }`}>
                                    Strength: {passwordStrength.toUpperCase()}
                                </span>
                            </div>
                            {passwordErrors.length > 0 && (
                                <ul className="text-xs text-red-600 ml-6 list-disc">
                                    {passwordErrors.map((err, idx) => (
                                        <li key={idx}>{err}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                <Input
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    required
                    error={formError.confirmPassword}
                />
                <div className="flex gap-2 mt-2 justify-end">
                    <Button type="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={handleSubmit} loading={loading} disabled={loading}>
                        Change Password
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default ChangePasswordModal;
